import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { formatDateBR, formatMoneyBR } from "../../utils/format";

type BoletoImport = {
  id: number;
  status: string;
  source: string;
  createdAt: string;
  importType: string;
  lineDigitavel: string | null;
  barcode: string | null;
  amount: number | null;
  principalAmount: number | null;
  fineAmount: number | null;
  interestAmount: number | null;
  totalAmount: number | null;
  paymentPrincipalLabel: string | null;
  dueDate: string | null;
  bankCode: string | null;
  beneficiaryName: string | null;
  beneficiaryDoc: string | null;
  payerName: string | null;
  payerDoc: string | null;
  pixCopiaECola: string | null;
  pixTxId: string | null;
  extractionJson: Record<string, unknown> | null;
  errorsJson: string[] | null;
};

export const BoletoImportDetailPage: React.FC = () => {
  const { id } = useParams();
  const [data, setData] = useState<BoletoImport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copyStatus, setCopyStatus] = useState<string | null>(null);

  const apiUrl = import.meta.env.VITE_API_URL as string;
  const token = useMemo(
    () => localStorage.getItem("mpf_admin_access_token"),
    []
  );

  useEffect(() => {
    const load = async () => {
      if (!id) {
        setError("ID invalido.");
        return;
      }
      setLoading(true);
      setError(null);
      setCopyStatus(null);
      try {
        const response = await fetch(`${apiUrl}/api/boleto-imports/${id}`, {
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        const payload = (await response.json()) as {
          data?: BoletoImport;
          error?: string;
        };
        if (!response.ok) {
          throw new Error(payload.error || "Falha ao carregar boleto.");
        }
        setData(payload.data ?? null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Erro ao carregar boleto.");
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [apiUrl, id, token]);

  const rawErrors = Array.isArray(data?.errorsJson) ? data?.errorsJson : [];
  const warnings = rawErrors.filter((item) => item.startsWith("warning:"));
  const errorsList = rawErrors.filter((item) => !item.startsWith("warning:"));
  const pixValue = data?.pixCopiaECola ?? null;
  const displayAmount =
    data?.totalAmount ?? data?.amount ?? data?.principalAmount ?? null;
  const expiresAt = data?.createdAt
    ? new Date(new Date(data.createdAt).getTime() + 30 * 24 * 60 * 60 * 1000)
    : null;

  const documentType =
    (data?.extractionJson as { documentType?: string } | null)?.documentType ??
    "UNKNOWN";

  const formatLinhaDigitavel = (value: string | null) => {
    if (!value) {
      return "-";
    }
    const digits = value.replace(/\D/g, "");
    if (digits.length !== 47) {
      return value;
    }
    return (
      `${digits.slice(0, 5)}.${digits.slice(5, 10)} ` +
      `${digits.slice(10, 15)}.${digits.slice(15, 21)} ` +
      `${digits.slice(21, 26)}.${digits.slice(26, 32)} ` +
      `${digits.slice(32, 33)} ${digits.slice(33)}`
    );
  };

  const handleCopyPix = async () => {
    if (!pixValue) {
      return;
    }
    try {
      await navigator.clipboard.writeText(pixValue);
      setCopyStatus("Pix copiado.");
    } catch {
      setCopyStatus("Falha ao copiar Pix.");
    }
  };

  return (
    <div className="container py-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h1 className="mb-0">Boleto importado</h1>
        <Link className="btn btn-outline-secondary" to="/financeiro/boletos/importar">
          Voltar
        </Link>
      </div>

      {loading && <div className="alert alert-info">Carregando...</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      {data && (
        <div className="card shadow-sm">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-3">
                <div className="text-muted">Status</div>
                <div>{data.status}</div>
              </div>
              <div className="col-md-3">
                <div className="text-muted">Origem</div>
                <div>{data.source}</div>
              </div>
              <div className="col-md-3">
                <div className="text-muted">Tipo selecionado</div>
                <div>{data.importType}</div>
              </div>
              <div className="col-md-3">
                <div className="text-muted">Tipo documento</div>
                <div>{documentType}</div>
              </div>
              <div className="col-md-3">
                <div className="text-muted">Data importacao</div>
                <div>{formatDateBR(data.createdAt)}</div>
              </div>
              <div className="col-md-3">
                <div className="text-muted">Expira em</div>
                <div>{expiresAt ? formatDateBR(expiresAt) : "-"}</div>
              </div>
              <div className="col-md-6">
                <div className="text-muted">Linha digitavel</div>
                <div>
                  {documentType === "BOLETO_BANCARIO"
                    ? formatLinhaDigitavel(data.lineDigitavel)
                    : data.lineDigitavel ?? "-"}
                </div>
              </div>
              <div className="col-md-6">
                <div className="text-muted">Codigo de barras</div>
                <div>{data.barcode ?? "-"}</div>
              </div>
              <div className="col-md-8">
                <div className="text-muted">Pix copia e cola</div>
                <div className="d-flex align-items-center gap-2">
                  <div className="text-break">{pixValue ?? "-"}</div>
                  {pixValue && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={handleCopyPix}
                    >
                      Copiar Pix
                    </button>
                  )}
                </div>
                {copyStatus && (
                  <div className="small text-muted mt-1">{copyStatus}</div>
                )}
              </div>
              <div className="col-md-4">
                <div className="text-muted">Pix txid</div>
                <div>{data.pixTxId ?? "-"}</div>
              </div>
              <div className="col-md-3">
                <div className="text-muted">Valor total</div>
                <div>
                  {displayAmount != null ? formatMoneyBR(displayAmount) : "-"}
                </div>
              </div>
              <div className="col-md-3">
                <div className="text-muted">Vencimento</div>
                <div>{data.dueDate ? formatDateBR(data.dueDate) : "-"}</div>
              </div>
              <div className="col-md-3">
                <div className="text-muted">Valor principal</div>
                <div>
                  {data.principalAmount != null
                    ? formatMoneyBR(data.principalAmount)
                    : "-"}
                </div>
              </div>
              <div className="col-md-3">
                <div className="text-muted">Multa</div>
                <div>
                  {data.fineAmount != null ? formatMoneyBR(data.fineAmount) : "-"}
                </div>
              </div>
              <div className="col-md-3">
                <div className="text-muted">Juros</div>
                <div>
                  {data.interestAmount != null
                    ? formatMoneyBR(data.interestAmount)
                    : "-"}
                </div>
              </div>
              <div className="col-md-6">
                <div className="text-muted">Pagamento principal</div>
                <div>{data.paymentPrincipalLabel ?? "-"}</div>
              </div>
              <div className="col-md-3">
                <div className="text-muted">Banco</div>
                <div>{data.bankCode ?? "-"}</div>
              </div>
              <div className="col-md-6">
                <div className="text-muted">Beneficiario</div>
                <div>{data.beneficiaryName ?? "-"}</div>
              </div>
              <div className="col-md-6">
                <div className="text-muted">Documento beneficiario</div>
                <div>{data.beneficiaryDoc ?? "-"}</div>
              </div>
              <div className="col-md-6">
                <div className="text-muted">Pagador</div>
                <div>{data.payerName ?? "-"}</div>
              </div>
              <div className="col-md-6">
                <div className="text-muted">Documento pagador</div>
                <div>{data.payerDoc ?? "-"}</div>
              </div>
            </div>

            <div className="mt-4">
              <h6>Erros</h6>
              {warnings.length > 0 && (
                <div className="alert alert-warning">
                  {warnings.map((item, index) => (
                    <div key={`${item}-${index}`}>{item.replace("warning: ", "")}</div>
                  ))}
                </div>
              )}
              {errorsList.length === 0 && (
                <div className="text-muted">Nenhum erro registrado.</div>
              )}
              {errorsList.length > 0 && (
                <ul>
                  {errorsList.map((item, index) => (
                    <li key={`${item}-${index}`}>{item}</li>
                  ))}
                </ul>
              )}
            </div>

            <div className="mt-4">
              <h6>Extracao</h6>
              <pre className="bg-light p-3 rounded border">
{JSON.stringify(data.extractionJson ?? {}, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
